import { ref, computed } from 'vue';
import store from '@/store';
import { config } from '@/config';
import { t } from '@/utils/i18n-helper';

/**
 * useDiaAbonadoSga
 *
 * Proceso público del día del abonado: el usuario llega desde el listado de
 * partidos, no desde su perfil. Se entra por el evento y el sistema resuelve
 * cuáles de sus abonos tienen suplemento en ese partido.
 *
 * Flujo:
 *   1. cargarAbonos() → abonos del usuario logueado con suplemento disponible.
 *      Si solo hay uno, el componente salta directo al resumen; si hay varios,
 *      se listan para que elija.
 *   2. comprar(abono) → crea el carrito en SGA y devuelve el importe y los
 *      datos del TPV.
 *
 * El usuario tiene que estar logueado: sin sesión no hay abonos que listar.
 *
 * @returns {Object} refs y métodos del proceso.
 */
export function useDiaAbonadoSga() {
    const loading = ref(false);
    const error = ref(null);
    const abonos = ref([]);

    // Pago
    const comprando = ref(false);
    const cancelando = ref(false);
    const cartId = ref(null);
    const sessionId = ref(null);
    const totalPrice = ref(null);
    const price = ref(null);
    const priceManagement = ref(null);
    const eventName = ref(null);
    const jetId = ref(null);
    const formularioPago = ref(null);

    // Promesa del DELETE en curso, para que un doble clic no lo repita.
    let cancelInFlight = null;

    const hayFormulario = computed(() => !!formularioPago.value);
    const hayJetId = computed(() => !!jetId.value);
    const sinAbonos = computed(() => !loading.value && abonos.value.length === 0);

    /**
     * Indica si el usuario tiene sesión activa.
     */
    const isAuthenticated = computed(() => store.getters.isLogged);

    /**
     * Abonos del usuario que tienen suplemento en este partido.
     *
     * @param {Number|String} eventoId
     */
    const cargarAbonos = async eventoId => {
        loading.value = true;
        error.value = null;

        try {
            const response = await store.dispatch(
                config.getVuexAlias('sga.evento.dia-abonado.abonos'),
                { evento_id: eventoId }
            );

            abonos.value = response.data?.abonos || [];
            return abonos.value;
        } catch (err) {
            error.value = t('front.sga.dia_abonado.error_abonos');
            abonos.value = [];
            throw err;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Crea el carrito del suplemento para el abono elegido.
     *
     * @param {Object} abono
     * @param {Number|String} eventoId
     */
    const comprar = async(abono, eventoId) => {
        comprando.value = true;
        error.value = null;

        try {
            const response = await store.dispatch(
                config.getVuexAlias('sga.evento.dia-abonado.comprar'),
                {
                    evento_id: eventoId,
                    abonado_id: abono.abonado_id,
                    entitlement_id: abono.entitlement_id,
                }
            );

            const data = response.data || {};

            cartId.value = data.cart_id;
            sessionId.value = data.session_id;
            totalPrice.value = data.total_price;
            price.value = data.price;
            priceManagement.value = data.price_management;
            eventName.value = data.event_name;
            jetId.value = data.jet_id;
            formularioPago.value = data.formulario;

            return data;
        } catch (err) {
            error.value =
                err?.response?.data?.message || t('front.sga.dia_abonado.error_compra');
            throw err;
        } finally {
            comprando.value = false;
        }
    };

    /**
     * Limpia el estado del pago en curso.
     */
    const resetPago = () => {
        cartId.value = null;
        sessionId.value = null;
        totalPrice.value = null;
        price.value = null;
        priceManagement.value = null;
        jetId.value = null;
        formularioPago.value = null;
    };

    /**
     * Cancela la compra en curso: borra el carrito creado en SGA y limpia el
     * estado local, para que el usuario pueda volver a elegir abono sin dejar
     * carritos huérfanos en SGA.
     *
     * Idempotente: si ya hay una cancelación en curso devuelve la misma
     * promesa. Un 404 se trata como éxito (el carrito ya no existe).
     *
     * No se llama al desmontar: tras pagar se navega a la pantalla de compra
     * finalizada y el componente se destruye; allí el carrito ya está pagado y
     * borrarlo sería un error.
     */
    const cancelar = () => {
        if (cancelInFlight) return cancelInFlight;

        if (!cartId.value) {
            resetPago();
            return Promise.resolve();
        }

        cancelando.value = true;

        cancelInFlight = store.dispatch(
            config.getVuexAlias('sga.cart.eliminar-datos'),
            { cart_id: cartId.value }
        )
            .then(() => {
                resetPago();
            })
            .catch(err => {
                if (err?.response?.status === 404) {
                    resetPago();
                    return;
                }
                throw err;
            })
            .finally(() => {
                cancelando.value = false;
                cancelInFlight = null;
            });

        return cancelInFlight;
    };

    return {
        loading,
        error,
        abonos,
        comprando,
        cancelando,
        cartId,
        sessionId,
        totalPrice,
        price,
        priceManagement,
        eventName,
        jetId,
        formularioPago,
        hayFormulario,
        hayJetId,
        sinAbonos,
        isAuthenticated,
        cargarAbonos,
        comprar,
        cancelar,
        resetPago,
    };
}
